import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormTextArea } from 'ui/components/elements/FormTextArea/FormTextArea';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { FormMultiSelect } from 'ui/components/elements/FormMultiSelect/FormMultiSelect';
import { FormCheckbox } from 'ui/components/elements/FormCheckbox/FormCheckbox';
import { useModal } from 'ui/components/screens/modals/useModal';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import IconSearch from 'ui/images/form/search.svg';
import { CompanyNameSearch } from 'ui/components/screens/modals/CompanyNameSearch/CompanyNameSearch';
import { Preview } from './Preview';
import { mqUser } from 'utils/responsive';

const Container = styled.div`
    display: flex;
    gap: 5px;
    width: 100%;
    margin: 0 auto;

    ${mqUser('SP')} {
        flex-direction: column;
    }
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const Description = styled.div`
    font-size: 14px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 0 10px;
    width: 100%;
    margin: 0 auto;
`;

const EditContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    padding: 30px 10px;
    background-color: var(--white);
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
`;

const PrivateTxt = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const SearchButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dark-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 30px;
    cursor: pointer;
    padding: 15px 25px;
    width: fit-content;

    img {
        margin-left: 8px;
        width: 20px;
        height: 20px;
    }
`;

interface CompanyEditFormProps {
    user: any;
    control: any;
    loading: boolean;
    errors: any;
    areaChoices: any[];
    employeeChoices: any[];
    revenueChoices: any[];
    industryChoices: any[];
    agentTypeChoices: any[];
    editing?: boolean;
    submit: () => void;
    setValue: (name: any, value: any) => void;
    watch: (name?: string) => any;
    onCompanySelect: (company: any) => void;
    setEditing?: (editing: boolean) => void;
}

export const CompanyEditForm: React.FC<CompanyEditFormProps> = ({
    user,
    control,
    loading,
    errors,
    areaChoices,
    employeeChoices,
    industryChoices,
    agentTypeChoices,
    editing = true,
    submit,
    setValue,
    watch,
    onCompanySelect,
    setEditing,
}) => {
    const companySearchModal = useModal();

    if (!user) {
        return null;
    }

    const onChangeIsSoleProprietor = () => {
        setValue('company_code', '');
        setValue('is_sole_proprietor', !watch('is_sole_proprietor'));
    };

    return (
        <>
            {companySearchModal.createModal(
                <CompanyNameSearch
                    onSelect={(company) => onCompanySelect(company)}
                    closeModal={companySearchModal.closeModal}
                />,
                { scrollable: false },
            )}

            <Container>
                {editing && (
                    <EditContainer>
                        <FlexContainer>
                            <FormLabel title="企業種別" isRequired />
                            {agentTypeChoices && agentTypeChoices.length > 0 && (
                                <Controller
                                    name="agent_type"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={agentTypeChoices.map(({ value, label }) => ({
                                                    value,
                                                    label,
                                                }))}
                                            />
                                            {errors.agent_type && <FormError message={errors.agent_type.message} />}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormCheckbox
                                id="is_sole_proprietor"
                                checked={!!watch('is_sole_proprietor')}
                                onChange={onChangeIsSoleProprietor}
                            >
                                <Description>個人事業主の方はこちらをチェックしてください</Description>
                            </FormCheckbox>
                        </FlexContainer>
                        {!user?.agent?.company?.name && !watch('is_sole_proprietor') && (
                            <FlexContainer>
                                <SearchButton
                                    onClick={() => {
                                        companySearchModal.openModal();
                                    }}
                                >
                                    法人を検索する
                                    <img src={IconSearch} alt="search" />
                                </SearchButton>
                            </FlexContainer>
                        )}
                        <FlexContainer>
                            <FormLabel
                                title={watch('is_sole_proprietor') ? '個人事業主名（屋号）' : '企業名'}
                                isRequired
                            />
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} hasError={!!errors.name?.message} />
                                        {errors.name?.message && <FormError message={errors.name.message} />}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        {!watch('is_sole_proprietor') && (
                            <FlexContainer>
                                <FormLabel title="法人番号" isRequired />
                                <Description>※法人を検索すると自動で入力されます</Description>
                                <Controller
                                    name="company_code"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormInput {...field} hasError={!!errors.company_code?.message} disabled />
                                            {errors.company_code?.message && (
                                                <FormError message={errors.company_code.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </FlexContainer>
                        )}
                        <FlexContainer>
                            <FormLabel title="対応可能なエリア" isRequired />
                            {areaChoices && areaChoices.length > 0 && (
                                <Controller
                                    name="regions_served"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormMultiSelect
                                                name="regions_served"
                                                options={areaChoices}
                                                selected={field.value ?? []}
                                                enableAutoComplete
                                                placeholder="エリアを選択"
                                                hasError={!!errors.regions_served?.message}
                                                onChange={field.onChange}
                                            />
                                            {errors.regions_served && (
                                                <FormError message={errors.regions_served[0].message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="従業員規模" isRequired />
                            {employeeChoices && employeeChoices.length > 0 && (
                                <Controller
                                    name="number_of_employees"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={employeeChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.number_of_employees && (
                                                <FormError message={errors.number_of_employees.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="対応可能な業種" isRequired />
                            {industryChoices && industryChoices.length > 0 && (
                                <Controller
                                    name="industries_served"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormMultiSelect
                                                name="regions_served"
                                                options={industryChoices}
                                                selected={field.value ?? []}
                                                enableAutoComplete
                                                placeholder="業種を選択"
                                                hasError={!!errors.regions_served?.message}
                                                onChange={field.onChange}
                                            />
                                            {errors.industries_served && (
                                                <FormError message={errors.industries_served[0].message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="自己PR" isRequired />
                            <Description>※企業様に公開されます。</Description>
                            <Controller
                                name="business_description"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.business_description?.message}
                                        />
                                        {errors.business_description?.message && (
                                            <FormError message={errors.business_description.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ご請求先用 郵便番号" isRequired />
                            <Description>※マッチングするまで費用は発生しません。</Description>
                            <Controller
                                name="billing_zipcode"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} hasError={!!errors.billing_zipcode?.message} />
                                        {errors.billing_zipcode?.message && (
                                            <FormError message={errors.billing_zipcode.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ご請求先用 住所" isRequired />
                            <Controller
                                name="billing_address"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.billing_address?.message}
                                        />
                                        {errors.billing_address?.message && (
                                            <FormError message={errors.billing_address.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ご請求先用 電話番号" isRequired />
                            <Description>※ハイフンなしで入力してください。</Description>
                            <Controller
                                name="billing_phone"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} hasError={!!errors.billing_phone?.message} />
                                        {errors.billing_phone?.message && (
                                            <FormError message={errors.billing_phone.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ご請求先用 氏名" isRequired />
                            <Controller
                                name="billing_name"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} hasError={!!errors.billing_name?.message} />
                                        {errors.billing_name?.message && (
                                            <FormError message={errors.billing_name.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ご請求先用 メールアドレス" isRequired />
                            <Controller
                                name="billing_email"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} hasError={!!errors.billing_email?.message} />
                                        {errors.billing_email?.message && (
                                            <FormError message={errors.billing_email.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ホームページURL" />
                            <Controller
                                name="hp_url"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.hp_url?.message}
                                        />
                                        {errors.hp_url?.message && <FormError message={errors.hp_url.message} />}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="企業画像" />
                            <ImageUpload
                                imagePath={watch('icon_image') ?? ''}
                                directory="agent"
                                fileName={'icon'}
                                fileCategory="agent"
                                disabled={loading}
                                width={100}
                                height={100}
                                onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormCheckbox
                                id="private"
                                checked={!!watch('private')}
                                onChange={() => setValue('private', !watch('private'))}
                            >
                                <PrivateTxt>オファーした相手にのみ企業情報を公開する</PrivateTxt>
                                <Description>※有効にした場合、トップページの企業事例に公開されません。</Description>
                            </FormCheckbox>
                        </FlexContainer>

                        <ButtonContainer>
                            <ActionButton
                                disabled={loading}
                                onClick={submit}
                                bgColor="main-color-blue"
                                style={{ minWidth: '200px', maxWidth: '400px' }}
                            >
                                設定する
                            </ActionButton>
                        </ButtonContainer>
                    </EditContainer>
                )}

                <Preview
                    agent={watch()}
                    editing={editing}
                    setEditing={setEditing}
                    areaChoices={areaChoices}
                    employeeChoices={employeeChoices}
                    industryChoices={industryChoices}
                />
            </Container>
        </>
    );
};
